import React, { useCallback, useContext, useEffect, useState } from "react";
import { createClasses, IconButton, Link } from "@bloomberg/media-ui-core";
import { InfoStroke, Lightbulb } from "@bloomberg/media-ui-icons";
import { Popover } from "@bloomberg/media-ui-overlay";
import { ScreenContext } from "@foundation/screen";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import styles from "./styles.module.css";
import DonutChart from "~components/DonutChart";
import { usePortfolioPage } from "~context/PortfolioPageContext";
import { SECURITY_TYPE_TO_ASSET_TYPE } from "~constants";
import ShareFeedback from "~components/ShareFeedback";
import { DASHBOARD_v2_FEEDBACK_URL } from "~constants/Portfolio";

const COLOR_GRADIENTS = [
  "#FF008C",
  "#009D9A",
  "#9757FF",
  "#AE8E00",
  "#97542B",
  "#949494",
];

const DONUT_CHART_FEEDBACK_URL =
  "https://bmedia.iad1.qualtrics.com/jfe/form/SV_8e7mBA6X3KbDQYm";

type SecurityType = {
  name: string;
  id: string;
  percentage: number;
  offset: number;
  color: string;
};

type SecurityTypeName =
  | "Bond"
  | "Commodity"
  | "Currency"
  | "Fund"
  | "Index"
  | "Stock";

type SecurityTypeCount = {
  [key in SecurityTypeName]: number;
};

// a function which takes the breakpoint as input and returns the
// width and height of the chart as output
function getChartDimensions(
  mobile: boolean | null,
  largeDesktop: boolean | null
) {
  if (largeDesktop)
    return {
      chartSize: 200,
      innerRadius: 70,
      outerRadius: 100,
    };
  else if (mobile)
    return {
      chartSize: 162,
      innerRadius: 56,
      outerRadius: 81,
    };

  return { chartSize: 140, innerRadius: 49, outerRadius: 70 };
}

const makePlural = (str: string, count = 0) => {
  if (count <= 1) {
    return str;
  }

  return `${str}s`;
};

export default function SecurityTypeGrouping({
  dashboardData: _dashboardData,
  version: _version = "v1",
}: {
  dashboardData?: HTMLElement;
  version?: "v1" | "v2";
}) {
  const {
    breakpoints: { smallDesktop },
  } = useContext(ScreenContext);
  const { selectedPortfolio } = usePortfolioPage();
  const [securityTypes, setSecurityTypes] = useState<Array<SecurityType>>([]);
  const [element, setElement] = useState<Element | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const toggleTooltip = useCallback(() => {
    setShowTooltip((prevShowTooltip) => {
      const eventLabel = !prevShowTooltip
        ? "tooltip_symbol_chart_open"
        : "tooltip_symbol_chart_close";

      trackClickEvent("watchlistToolTip", eventLabel, "click", "engagement");

      return !prevShowTooltip;
    });
  }, []);

  const closeTooltip = useCallback(() => {
    trackClickEvent(
      "watchlistToolTip",
      "tooltip_symbol_chart_close",
      "click",
      "engagement"
    );
    setShowTooltip(false);
  }, []);

  const {
    breakpoints: { mobile, largeDesktop },
  } = useContext(ScreenContext);

  // reset watchlist chart data on watchlist selection change
  useEffect(() => {
    const securityCount = selectedPortfolio?.positions?.length;

    if (!securityCount) {
      setSecurityTypes([]);

      return;
    }

    const securityTypeCount =
      selectedPortfolio.positions.reduce<SecurityTypeCount>(
        (acc, position) => {
          const assetType = (SECURITY_TYPE_TO_ASSET_TYPE[
            position.security.SECTYPE
          ] || position.security.SECTYPE) as SecurityTypeName;

          return { ...acc, [assetType]: acc[assetType] + 1 };
        },
        {
          Bond: 0,
          Commodity: 0,
          Currency: 0,
          Fund: 0,
          Index: 0,
          Stock: 0,
        }
      );

    // populated security type data for rendering
    let currentSegmentOffset;
    let precedingSegmentTotal = 0;

    // sort security type groups from largest to smallest
    const sortedSecurityTypes = Object.fromEntries(
      Object.entries(securityTypeCount).sort(([, a], [, b]) => b - a)
    ) as SecurityTypeCount;

    const parsedSecurityTypes = Object.keys(sortedSecurityTypes).map(
      (type, index) => {
        currentSegmentOffset = !precedingSegmentTotal
          ? 25
          : 100 - precedingSegmentTotal + 25;
        const percentage =
          (securityTypeCount[type as SecurityTypeName] / securityCount) * 100;

        precedingSegmentTotal += percentage;

        return {
          name: type,
          id: type,
          percentage,
          offset: currentSegmentOffset,
          color: COLOR_GRADIENTS[index],
        };
      }
    );

    // update security types
    setSecurityTypes(parsedSecurityTypes);
  }, [selectedPortfolio?.portInfo.id, selectedPortfolio?.positions]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!securityTypes.length) {
    return null;
  }

  const { chartSize, innerRadius, outerRadius } = getChartDimensions(
    mobile,
    largeDesktop
  );

  const data = securityTypes.map((item) => {
    return {
      label: item.name,
      color: item.color,
      value: item.percentage,
    };
  });

  const containerClasses = createClasses([
    styles.securityTypeGroup,
    _version === "v2" ? styles.v2 : null,
  ]);

  const tippyText =
    _version !== "v2" ? (
      "See the breakdown of symbol types in your watchlist. Lots (or shares) aren’t considered at this time."
    ) : (
      <span>
        See the breakdown of symbol types in your watchlist. Lots (or shares)
        aren’t considered at this time.
        <br />
        <br />
        Have feedback?{" "}
        <ShareFeedback
          feedbackUrl={DASHBOARD_v2_FEEDBACK_URL}
          trackingEventLabel="survey_symbol_chart"
        />
      </span>
    );

  return (
    <div className={containerClasses}>
      <div className={styles.title}>
        Symbol Type{" "}
        <IconButton
          className={styles.info}
          size="small"
          onClick={toggleTooltip}
          ref={setElement}
        >
          <InfoStroke />
        </IconButton>
        {showTooltip && element && (
          <Popover
            Icon={<Lightbulb />}
            allowedPlacements={["top-start", "top-end"]}
            eyebrow="How does this work?"
            text={tippyText}
            anchorElement={element}
            onDismiss={closeTooltip}
            acceptZeroDimensionAnchors
          />
        )}
      </div>
      <div className={styles.investmentData}>
        <div className={styles.donut}>
          <DonutChart
            className={styles.donutChart}
            size={chartSize}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            data={data}
          >
            <text
              className={styles.symbolCount}
              x={outerRadius}
              y={outerRadius - 8}
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {selectedPortfolio?.positions.length}
            </text>
            <text
              className={styles.symbolCountText}
              x={outerRadius}
              y={outerRadius + 12}
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {makePlural("Symbol", selectedPortfolio?.positions.length)}
            </text>
          </DonutChart>
        </div>
        <div className={styles.securityTypeLegendsContainer}>
          <div className={styles.securityTypeLegends}>
            {securityTypes.map(
              ({ id, percentage, name, color: backgroundColor }) =>
                percentage > 0 && (
                  <div key={id} className={styles.securityTypeLegend}>
                    <div className={styles.iconTitleContainer}>
                      <div
                        className={styles.legendIcon}
                        style={{ backgroundColor }}
                      ></div>
                      <div className={styles.legendTitle}>{name}</div>
                    </div>
                    <div className={styles.legendValue} data-testid={name}>
                      {Math.round(percentage)}%
                    </div>
                  </div>
                )
            )}
          </div>
        </div>
      </div>
      <ShareFeedback
        className={styles.shareFeedback}
        feedbackUrl={DONUT_CHART_FEEDBACK_URL}
        feedbackText="Share feedback"
        showIcon
        trackingEventLabel="survey_symbol_chart"
      />
    </div>
  );
}
